import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  BadgeCheck,
  ShieldCheck,
  ArrowRight,
  Phone,
  Mail,
  Globe,
  ExternalLink,
  ClipboardList,
  Building2,
  Users,
  Sparkles,
} from "lucide-react";
import Seo, { buildBreadcrumbs, SITE_URL } from "@/components/Seo";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";

const PDF_HREF = "/government/capabilities-statement.pdf";

const GOVERNMENT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "GovernmentService",
  name: "Federal Contracting — SBA 8(a) Certified Meeting & Event Services",
  provider: {
    "@type": "Organization",
    name: "Magee Enterprises",
    url: SITE_URL,
    identifier: [
      { "@type": "PropertyValue", propertyID: "DUNS", value: "05-711-6753" },
      { "@type": "PropertyValue", propertyID: "CAGE", value: "GFKRBPZU7329" },
      { "@type": "PropertyValue", propertyID: "NAICS-Primary", value: "561920" },
    ],
    hasCredential: [
      "SBA 8(a) Certified",
      "Small Business Enterprise (SBE) A0128-76",
      "Minority Business Enterprise (MBE) A0405-95",
      "LGBTQ+ Business Enterprise A0503-11",
      "IATAN Accredited",
    ],
  },
  serviceType: [
    "Strategic Meeting Management",
    "Conference & Tradeshow Organization",
    "Event Technology",
    "Group Travel & Logistics",
    "Education & Professional Development",
  ],
  areaServed: "US",
  audience: {
    "@type": "GovernmentOrganization",
    name: "U.S. Federal Agencies, State & Local Governments, Prime Contractors",
  },
  url: `${SITE_URL}/government`,
  identifier: [
    { "@type": "PropertyValue", propertyID: "NAICS", value: "561920" },
    { "@type": "PropertyValue", propertyID: "NAICS", value: "541611" },
    { "@type": "PropertyValue", propertyID: "NAICS", value: "541613" },
    { "@type": "PropertyValue", propertyID: "NAICS", value: "541618" },
    { "@type": "PropertyValue", propertyID: "NAICS", value: "541690" },
    { "@type": "PropertyValue", propertyID: "NAICS", value: "561510" },
    { "@type": "PropertyValue", propertyID: "NAICS", value: "611410" },
    { "@type": "PropertyValue", propertyID: "NAICS", value: "611699" },
    { "@type": "PropertyValue", propertyID: "NAICS", value: "611710" },
    { "@type": "PropertyValue", propertyID: "NAICS", value: "721199" },
  ],
};

const CAPABILITIES_DOC_JSONLD = {
  "@context": "https://schema.org",
  "@type": "DigitalDocument",
  name: "Magee Enterprises Capabilities Statement 2026",
  description:
    "SBA 8(a) certified capabilities statement — NAICS codes, certifications, core competencies, and past performance.",
  encodingFormat: "application/pdf",
  fileFormat: "application/pdf",
  contentSize: "538 KB",
  datePublished: "2026-01-01",
  inLanguage: "en-US",
  url: `${SITE_URL}/government/capabilities-statement.pdf`,
  publisher: {
    "@type": "Organization",
    name: "Magee Enterprises",
    url: SITE_URL,
  },
};

const NAICS = [
  { code: "561920", name: "Convention & Trade Show Organizers" },
  { code: "541611", name: "Admin & General Management Consulting" },
  { code: "541613", name: "Marketing Consulting Services" },
  { code: "541618", name: "Other Management Consulting Services" },
  { code: "541690", name: "Scientific & Technical Consulting" },
  { code: "561510", name: "Travel Agencies" },
  { code: "611410", name: "Business & Secretarial Schools" },
  { code: "611699", name: "All Other Miscellaneous Schools" },
  { code: "611710", name: "Education Support Services" },
  { code: "721199", name: "All Other Traveler Accommodations" },
];

const CERTS = [
  { k: "SBA 8(a) Certified", v: "Federal minority-owned enterprise" },
  { k: "SBE", v: "Small Business Enterprise · A0128-76" },
  { k: "MBE", v: "Minority Business Enterprise · A0405-95" },
  { k: "LGBTQ+ BE", v: "LGBTQ+ Business Enterprise · A0503-11" },
  { k: "IATAN", v: "Intl. Association of Travel Agency Networks" },
];

const COMPETENCIES = [
  "Strategic Meeting Management & Special Event Planning",
  "Conference & Tradeshow Organizers",
  "Marketing Consulting Services",
  "Travel Agency Services (Group Air, Rail, Hotel)",
  "Education & Professional Development Training",
  "Complimentary Site Search & Negotiations",
  "Attendee Registration & Payment Collections",
  "Sponsorship Recruitment & Management",
  "Tradeshow & Exhibits Organizers",
  "EventTech Solutions & On-Site Logistics",
  "Food & Beverage Coordination",
  "Off-Sites, Dinners & Dine-Arounds",
];

const PAST_PERF = [
  { name: "International Employee Recognition", value: "$12M" },
  { name: "Enterprise Event & Training Software Systems", value: "$724K" },
  { name: "Tech Review Conference", value: "$679K" },
  { name: "Global BioConference", value: "$379K" },
];

export default function Government() {
  useEffect(() => {
    const prev = document.title;
    document.title =
      "Government Capabilities — SBA 8(a) Certified · Magee Enterprises";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="bg-white text-[var(--text)]">
      <Seo
        title="Government Capabilities Statement — SBA 8(a) Certified"
        description="Magee Enterprises federal capabilities statement — SBA 8(a) certified minority-owned enterprise. NAICS 561920 & 9 more codes, DUNS 05-711-6753, CAGE GFKRBPZU7329, past performance up to $12M. Downloadable PDF."
        path="/government"
        image="/logos/magee-enterprises-logo.png"
        keywords="SBA 8(a) certified, federal contracting, capabilities statement, NAICS 561920, meeting management, event technology, MBE, SBE, LGBTQ+ BE, government events, minority-owned, DUNS 057116753, CAGE GFKRBPZU7329"
        jsonLd={[
          GOVERNMENT_JSONLD,
          CAPABILITIES_DOC_JSONLD,
          buildBreadcrumbs([
            ["Home", "/"],
            ["Government Capabilities", "/government"],
          ]),
        ]}
      />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32 px-4 md:px-6">
        <div className="relative max-w-[1600px] mx-auto rounded-[28px] md:rounded-[36px] overflow-hidden bg-[#050A14]">
          <div className="absolute inset-0 grid-pattern-dark opacity-50" />
          <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute -bottom-40 -right-24 w-[620px] h-[620px] rounded-full bg-sky-500/20 blur-3xl" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
          <HexMark
            size={280}
            opacity={0.06}
            className="absolute -right-8 top-16 rotate-[16deg] pointer-events-none hidden lg:block"
          />

          <div className="relative px-5 md:px-14 pt-14 md:pt-20 pb-16 md:pb-20 grid grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* LEFT — copy */}
            <div className="col-span-12 lg:col-span-7">
              <Reveal>
                <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                  <ShieldCheck size={12} /> Federal Contracting · SBA 8(a) Certified
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1
                  data-testid="government-hero-h1"
                  className="hero-headline text-white mt-6 md:mt-8 leading-[0.95] text-[42px] sm:text-[56px] md:text-[70px] lg:text-[80px] tracking-[-0.03em] text-balance"
                >
                  Capabilities.{" "}
                  <span className="hero-accent">On record.</span>
                </h1>
              </Reveal>

              <Reveal delay={0.2}>
                <p className="mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-2xl">
                  Magee Enterprises is a{" "}
                  <span className="text-cyan-300 font-semibold">
                    federally certified SBA 8(a) minority-owned enterprise
                  </span>{" "}
                  delivering strategic meeting management, conference &amp;
                  tradeshow organization, event technology, and education
                  programs to federal agencies, associations, and Fortune 500
                  primes.
                </p>
              </Reveal>

              <Reveal delay={0.3}>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <a
                    href={PDF_HREF}
                    download
                    data-testid="government-hero-download"
                    className="btn-primary"
                  >
                    Download Capabilities (PDF){" "}
                    <Download size={16} />
                  </a>
                  <Link
                    to="/contact"
                    className="btn-dark backdrop-blur border-white/25"
                  >
                    <ArrowRight size={14} /> Contact Contracting
                  </Link>
                </div>
              </Reveal>

              {/* Codes strip */}
              <Reveal delay={0.4}>
                <div className="mt-10 md:mt-12 grid grid-cols-2 sm:grid-cols-3 max-w-lg gap-4 pt-6 border-t border-white/10 text-white">
                  {[
                    { k: "DUNS", v: "05-711-6753" },
                    { k: "CAGE", v: "GFKRBPZU7329" },
                    { k: "Primary NAICS", v: "561920" },
                  ].map((s) => (
                    <div key={s.k}>
                      <div className="font-mono-brand text-[9.5px] uppercase tracking-[0.24em] text-white/50">
                        {s.k}
                      </div>
                      <div className="mt-1.5 font-editorial text-[15px] md:text-[17px] leading-tight text-white">
                        {s.v}
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* RIGHT — PDF preview card */}
            <div className="col-span-12 lg:col-span-5 relative">
              <Reveal>
                <div
                  className="relative rounded-3xl bg-white/[0.04] border border-cyan-300/25 backdrop-blur-xl p-6 md:p-7 overflow-hidden"
                  style={{
                    boxShadow:
                      "0 30px 80px -30px rgba(6,182,212,0.5)",
                    transform:
                      "perspective(900px) rotateX(3deg) rotateY(-4deg)",
                  }}
                >
                  <div className="absolute -inset-4 -z-10 bg-cyan-500/15 blur-3xl rounded-3xl" />

                  {/* Header */}
                  <div className="flex items-center justify-between text-[10px] font-mono-brand uppercase tracking-[0.24em] text-white/60 pb-4 border-b border-white/10">
                    <span className="text-cyan-300">Document · 2026</span>
                    <span className="text-white/45">PDF · 538 KB</span>
                  </div>

                  {/* Icon + title */}
                  <div className="mt-6 flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shrink-0 shadow-[0_10px_30px_-10px_rgba(6,182,212,0.6)]">
                      <ClipboardList size={22} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-editorial text-white text-xl md:text-2xl leading-tight">
                        Capabilities Statement
                      </div>
                      <div className="mt-1 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-300/85">
                        Magee Enterprises · 2026 Edition
                      </div>
                    </div>
                  </div>

                  {/* Mini table of contents */}
                  <ul className="mt-6 space-y-2.5 text-white/75 text-[13px]">
                    {[
                      "Company overview & differentiators",
                      "NAICS codes & certifications",
                      "Core competencies",
                      "Past performance & clients",
                    ].map((t) => (
                      <li key={t} className="flex items-start gap-2.5">
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-cyan-400/80 shrink-0" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={PDF_HREF}
                    download
                    data-testid="government-pdf-card-download"
                    className="mt-6 flex items-center justify-between rounded-xl bg-white text-[var(--text)] px-4 py-3 hover:bg-cyan-50 transition-colors group"
                  >
                    <span className="font-editorial text-[14px]">
                      Download PDF
                    </span>
                    <Download
                      size={16}
                      className="text-cyan-600 group-hover:translate-y-0.5 transition-transform"
                    />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-32 -right-24 opacity-25" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip">
              <BadgeCheck size={12} /> Certifications
            </span>
            <h2 className="mt-6 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance">
              Diverse-supplier{" "}
              <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                credentials.
              </span>
            </h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CERTS.map((c, i) => (
              <Reveal key={c.k} delay={i * 0.05}>
                <div
                  data-testid={`gov-cert-${i + 1}`}
                  className="card-white p-6 h-full flex items-start gap-4"
                >
                  <div className="icon-tile shrink-0">
                    <BadgeCheck size={18} />
                  </div>
                  <div>
                    <div className="font-editorial text-lg leading-tight">
                      {c.k}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-muted)] leading-relaxed">
                      {c.v}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NAICS + CORE COMPETENCIES */}
      <section className="relative py-16 md:py-20 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-10">
          <Reveal className="col-span-12 lg:col-span-6">
            <span className="chip">
              <Building2 size={12} /> NAICS Codes
            </span>
            <h2 className="mt-6 font-editorial text-3xl md:text-4xl leading-[1.05] tracking-[-0.02em]">
              Registered classifications.
            </h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {NAICS.map((n) => (
                <div
                  key={n.code}
                  data-testid={`gov-naics-${n.code}`}
                  className="card-white p-4 flex items-start gap-3"
                >
                  <span className="font-mono-brand text-[11px] tracking-[0.12em] text-cyan-600 shrink-0 pt-0.5">
                    {n.code}
                  </span>
                  <span className="text-[12.5px] leading-snug text-[var(--text-muted)]">
                    {n.name}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="col-span-12 lg:col-span-6">
            <span className="chip">
              <Sparkles size={12} /> Core Competencies
            </span>
            <h2 className="mt-6 font-editorial text-3xl md:text-4xl leading-[1.05] tracking-[-0.02em]">
              What we deliver.
            </h2>
            <ul className="mt-6 grid grid-cols-1 gap-2">
              {COMPETENCIES.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-3 px-4 py-3 rounded-xl bg-white border border-[var(--border)] text-[13px] leading-relaxed"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-sky-600 shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* PAST PERFORMANCE */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] -bottom-24 -left-24 opacity-20" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip">
              <Users size={12} /> Past Performance
            </span>
            <h2 className="mt-6 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance">
              Programs on{" "}
              <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                the record.
              </span>
            </h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PAST_PERF.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.06}>
                <div
                  data-testid={`gov-past-${i + 1}`}
                  className="grad-border card-white p-5 h-full relative"
                >
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                    Program · 0{i + 1}
                  </div>
                  <div className="mt-3 font-editorial text-lg leading-tight">
                    {p.name}
                  </div>
                  <div className="mt-4 pt-4 border-t border-[var(--border)] flex items-baseline justify-between">
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Value
                    </span>
                    <span className="font-editorial text-2xl bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent">
                      {p.value}
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Contact */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden px-8 md:px-16 py-16 md:py-20 text-center">
            <div className="absolute inset-0 grid-pattern-dark opacity-40" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
            <div className="relative">
              <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                <ShieldCheck size={12} /> SBA 8(a) · Ready to Contract
              </span>
              <h2 className="mt-8 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] text-white max-w-4xl mx-auto text-balance">
                Ready to review our{" "}
                <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">
                  full capabilities.
                </span>
              </h2>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <a
                  href={PDF_HREF}
                  download
                  data-testid="government-cta-download"
                  className="btn-primary"
                >
                  Download PDF <Download size={16} />
                </a>
                <a
                  href={PDF_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dark border-white/20"
                >
                  <ExternalLink size={14} /> Open in browser
                </a>
              </div>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-white/80 text-sm max-w-3xl mx-auto">
                <a
                  href="tel:+18882992250"
                  className="flex items-center gap-3 justify-center hover:text-cyan-300 transition-colors"
                >
                  <Phone size={14} className="text-cyan-400" />
                  888-299-2250
                </a>
                <a
                  href="mailto:dion@mageeenterprises.com"
                  className="flex items-center gap-3 justify-center hover:text-cyan-300 transition-colors"
                >
                  <Mail size={14} className="text-cyan-400" />
                  dion@mageeenterprises.com
                </a>
                <a
                  href="https://mageeenterprises.com"
                  className="flex items-center gap-3 justify-center hover:text-cyan-300 transition-colors"
                >
                  <Globe size={14} className="text-cyan-400" />
                  mageeenterprises.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
